import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useCombobox, type UseComboboxProps } from 'downshift';

import { Popover } from '../popover';

import { ComboboxContext } from './context';
import type { ComboboxItemBase } from './types';

const { stateChangeTypes } = useCombobox;

/** The text that a search compares: lower case and without accents, so "curac" finds "Curaçao". */
const searchText = (text: string) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** The items whose label holds the typed text, ignoring case and accents. */
export const filterByLabel = (inputValue: string, items: ComboboxItemBase[]) => {
  const typed = searchText(inputValue);
  return items.filter(item => !typed || searchText(item.label).includes(typed));
};

export type ComboboxProps = PropsWithChildren<{
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  filterItems?: (
    inputValue: string,
    items: ComboboxItemBase[]
  ) => ComboboxItemBase[];
}>;

export const Combobox = ({
  value,
  onValueChange,
  filterItems = filterByLabel,
  children,
}: ComboboxProps) => {
  const [items, setItems] = useState<ComboboxItemBase[]>([]),
    [filteredItems, setFilteredItems] = useState<ComboboxItemBase[]>(items);
  const [openedOnce, setOpenedOnce] = useState(false);

  const stateReducer = useCallback<
    NonNullable<UseComboboxProps<ComboboxItemBase>['stateReducer']>
  >(
    (prev, { type, changes }) => {
      switch (type) {
        case stateChangeTypes.InputChange: {
          const filteredEnabledItems = filterItems(
            changes.inputValue || prev.inputValue,
            items
          ).filter(({ disabled }) => !disabled);
          const highlightedIndex =
            typeof changes.highlightedIndex === 'number'
              ? changes.highlightedIndex
              : prev.highlightedIndex;

          return {
            ...changes,
            highlightedIndex:
              changes.inputValue &&
              filteredEnabledItems.length > 0 &&
              highlightedIndex < 0
                ? 0
                : changes.highlightedIndex,
          };
        }

        case stateChangeTypes.InputBlur:
        case stateChangeTypes.InputClick:
        case stateChangeTypes.InputKeyDownEnter:
        case stateChangeTypes.InputKeyDownEscape: {
          if (changes.isOpen || !prev.isOpen)
            return {
              ...changes,
              inputValue: prev.inputValue,
              selectedItem: prev.selectedItem,
            };
          if (!prev.inputValue && prev.highlightedIndex < 0)
            return { ...changes, inputValue: '', selectedItem: null };

          const inputValue =
            changes.selectedItem?.label || prev.selectedItem?.label || '';
          return { ...changes, inputValue };
        }

        default:
          return changes;
      }
    },
    [filterItems, items]
  );

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    inputValue,
    isOpen,
    selectedItem,
    selectItem,
    setInputValue,
  } = useCombobox({
    items: filteredItems,
    itemToString: item => (item ? item.label : ''),
    isItemDisabled: item => item.disabled ?? false,

    selectedItem:
      typeof value !== 'undefined'
        ? items.find(item => item.value === value) || null
        : undefined,
    onSelectedItemChange: ({ selectedItem }) =>
      onValueChange?.(selectedItem?.value || null),

    stateReducer,
  });

  useEffect(() => {
    if (isOpen && !openedOnce) setOpenedOnce(isOpen);
  }, [isOpen, openedOnce]);

  useEffect(() => {
    setFilteredItems(filterItems(inputValue, items));
  }, [filterItems, inputValue, items]);

  return (
    <ComboboxContext.Provider
      value={{
        filteredItems,
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        inputValue,
        isOpen,
        items,
        onItemsChange: setItems,
        onValueChange,
        openedOnce,
        selectedItem,
        selectItem,
        setInputValue,
      }}
    >
      <Popover open={isOpen}>{children}</Popover>
    </ComboboxContext.Provider>
  );
};
